import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Waves, 
  Wifi, 
  Car, 
  Wind, 
  Utensils, 
  Gamepad2, 
  Baby, 
  Accessibility, 
  PawPrint, 
  Bed, 
  Bath, 
  Leaf,
  Coffee,
  Microwave,
  Refrigerator,
  FlameKindling,
  CreditCard,
  WashingMachine,
  ShowerHead,
  Check,
  Search,
  Monitor,
  Music,
  Tv,
  Users,
  Sun,
  Camera,
  ChefHat,
  Loader2
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

const iconMap: Record<string, React.ElementType> = {
  Waves, Wifi, Car, Wind, Utensils, Gamepad2, Baby, Accessibility, 
  PawPrint, Bed, Bath, Leaf, Coffee, Microwave, Refrigerator, 
  FlameKindling, CreditCard, WashingMachine, ShowerHead, Check, 
  Search, Monitor, Music, Tv, Users, Sun, Camera, ChefHat
};

const sustainabilityIcons: Record<string, React.ElementType> = {
  'ENERGY EFFICIENCY': Leaf,
  'WATER CONSERVATION': Leaf,
  'WASTE REDUCTION': Leaf,
};

export function Amenities() {
  const [amenities, setAmenities] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAmenities = async () => {
      const { data } = await supabase
        .from('amenities')
        .select('*')
        .eq('is_active', true)
        .order('category', { ascending: true })
        .order('title', { ascending: true });
      
      if (data) setAmenities(data);
      setLoading(false);
    };
    fetchAmenities();
  }, []);

  if (loading && amenities.length === 0) {
    return (
      <section className="py-24 bg-secondary flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#176B5B]" />
      </section>
    );
  }

  const popularAmenities = amenities.filter(a => a.category === 'Popular Amenities');
  
  const facilityCategories = [
    'Internet', 'Food & Drink', 'Policies & Payments', 'Activities', 
    'Services', 'Children', 'Pools', 'Parking & Transportation', 
    'Wellness', 'Accessibility', 'Pets', 'Rooms'
  ];

  const sustainabilityCategories = [
    'Sustainability: Energy', 'Sustainability: Water', 'Sustainability: Waste'
  ];

  const facilityGroups = facilityCategories.map(cat => ({
    title: cat.toUpperCase(),
    items: amenities.filter(a => a.category === cat),
    icon: iconMap[amenities.find(a => a.category === cat)?.icon_name] || Check
  })).filter(group => group.items.length > 0);

  const sustainabilityGroups = sustainabilityCategories.map(cat => ({
    title: cat.replace('Sustainability: ', '').toUpperCase() + (cat.includes('Energy') ? ' EFFICIENCY' : cat.includes('Water') ? ' CONSERVATION' : ' REDUCTION'),
    items: amenities.filter(a => a.category === cat)
  })).filter(group => group.items.length > 0);

  return (
    <section id="amenities" className="pt-12 lg:pt-20 pb-24 lg:pb-40 bg-secondary px-6">
      <div className="max-w-screen-xl mx-auto">
        {/* Header */}
        <motion.div 
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16 lg:mb-24 space-y-6"
        >
          <span className="font-serif !text-[#176B5B] text-xl md:text-2xl lowercase italic">
            amenities & facilities
          </span>
          <h2 className="heading-luxury max-w-4xl mx-auto font-bold !text-[#176B5B]">
            Everything You Need for a <span className="italic">Perfect Stay</span>
          </h2>
          <p className="max-w-2xl mx-auto font-clean text-base md:text-lg leading-relaxed !text-[#59635C]">
            Discover thoughtfully selected facilities designed to make your stay at Walnut Farm comfortable, relaxing and memorable.
          </p>
        </motion.div>

        {/* 1. Popular Amenities */}
        <div className="mb-20">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
            {popularAmenities.map((item) => {
              const Icon = iconMap[item.icon_name] || Check;
              return (
                <motion.div
                  key={item.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  className="bg-background p-6 md:p-8 rounded-2xl border border-accent/20 shadow-sm flex flex-col items-center justify-center text-center space-y-4 hover:border-accent/50 hover:shadow-md transition-all duration-300 group"
                >
                  <div className="text-primary group-hover:scale-110 transition-transform duration-300">
                    <Icon className="w-8 h-8 md:w-10 md:h-10" />
                  </div>
                  <span className="font-clean text-sm md:text-base font-bold uppercase tracking-widest !text-[#176B5B]">
                    {item.title}
                  </span>
                </motion.div>
              );
            })}
          </div>
        </div>

        {/* 2. Facilities & Services */}
        <div className="mb-24 lg:mb-32">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-y-12 gap-x-8">
            {facilityGroups.map((group) => {
              const Icon = group.icon;
              return (
                <motion.div
                  key={group.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  className="flex flex-col"
                >
                  <div className="flex items-center space-x-3 mb-6">
                    <Icon className="w-5 h-5 text-accent" />
                    <h3 className="text-sm md:text-base font-bold uppercase tracking-widest !text-[#176B5B]">
                      {group.title}
                    </h3>
                  </div>
                  <ul className="space-y-3">
                    {group.items.map((item: any) => (
                      <li key={item.id} className="flex items-start space-x-3 font-clean text-sm md:text-base !text-[#59635C]">
                        <Check className="w-4 h-4 text-accent/60 mt-0.5 flex-shrink-0" />
                        <span className="leading-tight">{item.title}</span>
                      </li>
                    ))}
                  </ul>
                </motion.div>
              );
            })}
          </div>
        </div>

        {/* 3. Sustainability */}
        <div className="pt-16 border-t border-accent/10">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16 space-y-4"
          >
            <span className="font-serif !text-[#176B5B] text-xl lowercase italic">
              sustainability
            </span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold !text-[#176B5B]">
              Thoughtful Stays, <span className="italic">Responsible Choices</span>
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-y-12 gap-x-8">
            {sustainabilityGroups.map((group) => (
              <motion.div
                key={group.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="flex flex-col"
              >
                <h3 className="text-sm md:text-base font-bold uppercase tracking-widest !text-[#176B5B] mb-6">
                  {group.title}
                </h3>
                <ul className="space-y-3">
                  {group.items.map((item: any) => (
                    <li key={item.id} className="flex items-start space-x-3 font-clean text-sm md:text-base !text-[#59635C]">
                      <Check className="w-4 h-4 text-accent/60 mt-0.5 flex-shrink-0" />
                      <span className="leading-tight">{item.title}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
